import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { DashboardSection, DEFAULT_SECTION_ORDER } from '../types'

interface UIState {
  // UI Preferences (persisted to localStorage)
  theme: 'light' | 'dark'
  collapsedSections: Record<string, boolean>
  includeSecondaryMovers: boolean
  hideInactiveMuscleGroups: boolean
  muscleGroupDataType: 'oneRepMax' | 'bestWeight'
  showRecentExercisesOnly: boolean
  enabledSections: DashboardSection[]

  // Ephemeral UI state (not persisted)
  isDragging: boolean
  scrollTarget: string | null
  activeModal: string | null
  activeModalData: any | null // Store the actual data being displayed in the modal
  sidebarOpen: boolean
  isUpdating: Record<string, boolean>

  // Actions for UI preferences
  setTheme: (theme: 'light' | 'dark') => void
  toggleSection: (id: string) => void
  toggleSecondaryMovers: () => void
  toggleHideInactiveMuscleGroups: () => void
  setMuscleGroupDataType: (type: 'oneRepMax' | 'bestWeight') => void
  toggleShowRecentExercisesOnly: () => void
  setEnabledSections: (sections: DashboardSection[]) => void
  addSection: (section: DashboardSection) => void
  removeSection: (section: DashboardSection) => void
  resetSections: () => void

  // Actions for ephemeral state
  setDragging: (isDragging: boolean) => void
  setScrollTarget: (target: string | null) => void
  clearScrollTarget: () => void
  setActiveModal: (modal: string | null, data?: any) => void
  toggleSidebar: () => void
  setUpdating: (key: string, isUpdating: boolean) => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // UI Preferences (will be persisted)
      theme: 'dark' as const,
      collapsedSections: {},
      includeSecondaryMovers: false,
      hideInactiveMuscleGroups: false,
      muscleGroupDataType: 'oneRepMax' as const,
      showRecentExercisesOnly: true,
      enabledSections: DEFAULT_SECTION_ORDER,

      // Ephemeral UI state (will NOT be persisted)
      isDragging: false,
      scrollTarget: null,
      activeModal: null,
      activeModalData: null,
      sidebarOpen: false,
      isUpdating: {},

      // Actions for UI preferences
      setTheme: (theme) => {
        // Update store state
        set({ theme })

        // Update DOM
        const root = document.documentElement
        if (theme === 'dark') {
          root.classList.add('dark')
        } else {
          root.classList.remove('dark')
        }
      },

      toggleSection: (id) =>
        set((state) => ({
          collapsedSections: {
            ...state.collapsedSections,
            [id]: !state.collapsedSections[id]
          }
        })),

      toggleSecondaryMovers: () =>
        set((state) => ({
          includeSecondaryMovers: !state.includeSecondaryMovers
        })),

      toggleHideInactiveMuscleGroups: () =>
        set((state) => ({
          hideInactiveMuscleGroups: !state.hideInactiveMuscleGroups
        })),

      setMuscleGroupDataType: (type) => set({ muscleGroupDataType: type }),

      toggleShowRecentExercisesOnly: () =>
        set((state) => ({
          showRecentExercisesOnly: !state.showRecentExercisesOnly
        })),

      setEnabledSections: (sections) => set({ enabledSections: sections }),

      addSection: (section) =>
        set((state) => {
          if (!state.enabledSections.includes(section)) {
            return { enabledSections: [...state.enabledSections, section] }
          }
          return state
        }),

      removeSection: (section) =>
        set((state) => ({
          enabledSections: state.enabledSections.filter((s) => s !== section)
        })),

      resetSections: () => set({ enabledSections: DEFAULT_SECTION_ORDER }),

      // Actions for ephemeral state
      setDragging: (isDragging) => set({ isDragging }),
      setScrollTarget: (target) => set({ scrollTarget: target }),
      clearScrollTarget: () => set({ scrollTarget: null }),
      setActiveModal: (modal, data) => set({ activeModal: modal, activeModalData: data || null }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setUpdating: (key, isUpdating) =>
        set({
          isUpdating: {
            ...get().isUpdating,
            [key]: isUpdating
          }
        })
    }),
    {
      name: 'loadline-ui',
      // Only persist UI preferences, not ephemeral state
      partialize: (state) => ({
        theme: state.theme,
        collapsedSections: state.collapsedSections,
        includeSecondaryMovers: state.includeSecondaryMovers,
        hideInactiveMuscleGroups: state.hideInactiveMuscleGroups,
        muscleGroupDataType: state.muscleGroupDataType,
        showRecentExercisesOnly: state.showRecentExercisesOnly,
        enabledSections: state.enabledSections
      })
    }
  )
)
